import { Capacitor } from "@capacitor/core";
import { getSession, clearSession, setSession, type AuthSession } from "@/services/authStore";
import { refreshAccessToken } from "./authApi";

/**
 * Returns the API base URL.
 *
 * - Web browser: "" (relative URLs, same origin as the dev server or backend)
 * - Native (Android/iOS) with live-reload: "" works because Capacitor loads
 *   the app from the Vite dev server, which proxies /api to the backend.
 * - Native without live-reload (production build): set VITE_API_BASE_URL in
 *   .env.local to the backend address (e.g. https://api.example.com) so API
 *   calls reach the backend instead of capacitor://localhost.
 */
const resolveBaseUrl = (): string => {
  if (Capacitor.isNativePlatform()) {
    const configured = import.meta.env.VITE_API_BASE_URL;
    if (typeof configured === "string" && configured.length > 0) {
      return configured;
    }

    if (typeof window !== "undefined") {
      return window.location.origin;
    }

    return "";
  }
  return "";
};

/**
 * Track ongoing refresh to prevent race conditions
 */
let refreshPromise: Promise<AuthSession | null> | null = null;

/**
 * Refresh access token using stored refresh token
 * This function is memoized to prevent concurrent refresh attempts
 *
 * @returns New session data or null if refresh fails
 */
const refreshSession = async (): Promise<AuthSession | null> => {
  // If a refresh is already in progress, return that promise
  if (refreshPromise) {
    return refreshPromise;
  }

  // Start new refresh
  refreshPromise = (async (): Promise<AuthSession | null> => {
    try {
      const currentSession = await getSession();
      if (!currentSession?.refreshToken) {
        throw new Error("No refresh token available");
      }

      // Call backend refresh endpoint
      const refreshedTokens = await refreshAccessToken(
        currentSession.refreshToken
      );

      const authResponse = {
        token: refreshedTokens.token,
        refreshToken: refreshedTokens.refreshToken,
        user: currentSession.user,
      };

      // Store new session
      await setSession(authResponse);

      // Build the AuthSession shape for the return value
      const newSession: AuthSession = {
        token: authResponse.token,
        refreshToken: authResponse.refreshToken,
        user: authResponse.user,
        createdAt: new Date().toISOString(),
      };

      return newSession;
    } catch (error) {
      // On refresh failure, clear session
      await clearSession();

      // Redirect to signin
      if (typeof window !== "undefined") {
        window.location.href = "/signin";
      }

      return null;
    } finally {
      // Clear promise so new refresh can be attempted
      refreshPromise = null;
    }
  })();

  return refreshPromise;
};

/**
 * Enhanced fetch function for API calls with JWT authentication
 * Handles automatic token refresh on 401 errors
 *
 * @param input - URL or Request object
 * @param init - Fetch options
 * @returns Response object
 */
export async function apiFetch(
  input: string,
  init: RequestInit = {},
): Promise<Response> {
  const baseUrl = resolveBaseUrl();
  const url = input.startsWith("http") ? input : `${baseUrl}${input}`;

  const session = await getSession();
  const headers = new Headers(init.headers);
  if (session?.token && !headers.has("Authorization")) {
    headers.set("Authorization", `Bearer ${session.token}`);
  }

  const response = await fetch(url, { ...init, headers });

  // Handle 401 Unauthorized - attempt token refresh
  if (response.status === 401 && session?.refreshToken) {
    // Don't attempt refresh for refresh endpoint itself
    if (url.includes("/auth/refresh")) {
      await clearSession();
      if (typeof window !== "undefined") {
        window.location.href = "/signin";
      }
      return response;
    }

    // Refresh token and retry request
    const newSession = await refreshSession();

    if (newSession) {
      // Retry original request with new token
      const retryHeaders = new Headers(init.headers);
      retryHeaders.set("Authorization", `Bearer ${newSession.token}`);

      return fetch(url, { ...init, headers: retryHeaders });
    }
  }

  return response;
}

/**
 * Reset refresh promise (useful for testing)
 */
export function resetRefreshPromise(): void {
  refreshPromise = null;
}

/**
 * Create a typed settings client
 * Settings sync is independent from main data sync
 */
export function createSettingsClient(fetch: typeof window.fetch) {
  const baseUrl = resolveBaseUrl();
  const settingsBaseUrl = `${baseUrl}/api/settings`;

  return {
    /**
     * Get all settings from server
     */
    async getAll() {
      const session = await getSession();
      const headers: Record<string, string> = {};
      if (session?.token) {
        headers['Authorization'] = `Bearer ${session.token}`;
      }

      const response = await fetch(`${settingsBaseUrl}`, {
        headers,
      });

      if (!response.ok) {
        throw new Error(`Settings fetch failed: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      return data;
    },

    /**
     * Sync settings with server
     * This is separate from the main data sync endpoint
     */
    async sync(settings: any[], lastSyncTimestamp?: string) {
      const session = await getSession();
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      if (session?.token) {
        headers['Authorization'] = `Bearer ${session.token}`;
      }

      const response = await fetch(`${settingsBaseUrl}/sync`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ settings, lastSyncTimestamp }),
      });

      if (!response.ok) {
        throw new Error(`Settings sync failed: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      return data;
    },
  };
}
